/* ═══════════════════════════════════════════════════════════════
   WChart — the ONE recharts wrapper + colour bridge every widget body
   uses. `tone` (a status/series identity, never a raw color) maps to a
   base.css token var here and nowhere else (framework rule #2: bridge
   third-party CSS vars to tokens in one place). axisTick / tooltip
   style live here too — SVG text props take numbers, not CSS vars, so
   `axisTick.fontSize` is this file's one sanctioned raw literal.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { rem } from '../../lib/rem';

export type ChartTone = 'ok' | 'warn' | 'danger' | 'info' | 'neutral' | 'forecast' | 'accent';

const TONE_VAR: Record<ChartTone, string> = {
  ok: 'var(--color-success)',
  warn: 'var(--color-warning)',
  danger: 'var(--color-danger)',
  info: 'var(--color-info)',
  neutral: 'var(--color-text-subtle)',
  // Series-identity color (purple), not a severity token — matches the
  // pre-existing COLOR_FORECAST exception in the report-widget shared file.
  forecast: 'var(--color-smart)',
  accent: 'var(--ac)',
};

export function toneToVar(tone: ChartTone | undefined): string {
  return TONE_VAR[tone ?? 'info'] ?? TONE_VAR.info;
}

export const axisTick = { fontSize: 10, fill: 'var(--tm)' };
export const chartTooltipStyle: React.CSSProperties = {
  background: 'var(--bg)',
  border: '1px solid var(--bd)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-base)',
};

export type ChartKind = 'bar' | 'line' | 'donut';

export interface ChartSeries {
  key: string;
  label: string;
  tone: ChartTone;
}

export interface WChartProps {
  kind: ChartKind;
  /** Row objects. For 'donut', a row may carry its own `tone` (falls back
   *  to cycling through `series` tones by index when it doesn't). */
  data: Array<Record<string, any>>;
  series: ChartSeries[];
  /** Category key for bar/line x-axis, or the name key for donut slices. */
  xKey?: string;
  /** Chart height in px (via `rem()` — never a raw CSS px value). */
  height?: number;
  /** Bar click — `key` is the clicked bar's `xKey` value, `row` is its
   *  full data object (so a caller can drill on a field other than the
   *  displayed category, e.g. a raw date backing a short label). */
  onBarClick?: (key: string, row: Record<string, any>) => void;
  /** Line-chart point click — same contract as `onBarClick`. */
  onPointClick?: (key: string, row: Record<string, any>) => void;
  /** Custom tooltip value formatting (recharts formatter contract: returns
   *  `[displayValue, displayName]`). Defaults to recharts' raw display. */
  tooltipFormatter?: (value: any, series: ChartSeries) => [string, string];
}

export function WChart({
  kind,
  data,
  series,
  xKey,
  height = 160,
  onBarClick,
  onPointClick,
  tooltipFormatter,
}: WChartProps) {
  // Empty data → the quiet empty line, never bare axes.
  if (!data || data.length === 0) {
    return (
      <div
        className="flex w-full items-center justify-center text-sm text-muted"
        style={{ minHeight: rem(48) }}
      >
        No data yet
      </div>
    );
  }
  return (
    <div style={{ height: rem(height), width: '100%' }}>
      <ResponsiveContainer width="100%" height="100%">
        {kind === 'bar' ? (
          <BarChart
            data={data}
            margin={{ top: 4, right: 8, bottom: 0, left: -20 }}
            onClick={
              onBarClick
                ? (s: any) => {
                    const row = s?.activePayload?.[0]?.payload;
                    if (row) onBarClick(xKey ? row[xKey] : undefined, row);
                  }
                : undefined
            }
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)" />
            <XAxis dataKey={xKey} tick={axisTick} />
            <YAxis tick={axisTick} />
            <Tooltip
              contentStyle={chartTooltipStyle}
              formatter={
                tooltipFormatter
                  ? (v: any, _name: any, p: any) =>
                      tooltipFormatter(v, series.find((s) => s.key === p?.dataKey) ?? series[0])
                  : undefined
              }
            />
            {series.map((s) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.label}
                fill={toneToVar(s.tone)}
                radius={[3, 3, 0, 0]}
                cursor={onBarClick ? 'pointer' : undefined}
                onClick={
                  onBarClick
                    ? (data: any, _index: number, event: React.MouseEvent) => {
                        // Bar-level click fires on the actual rect regardless
                        // of hover/tooltip state (chart-level onClick above
                        // needs `activePayload`, which only populates after a
                        // mousemove — a synthetic click skips that). Stop the
                        // bubble so the chart-level handler doesn't also fire
                        // for the same physical click (exactly once).
                        event?.stopPropagation?.();
                        const row = data?.payload;
                        if (row) onBarClick(xKey ? row[xKey] : undefined, row);
                      }
                    : undefined
                }
              />
            ))}
          </BarChart>
        ) : kind === 'line' ? (
          <LineChart
            data={data}
            margin={{ top: 4, right: 8, bottom: 0, left: -20 }}
            onClick={
              onPointClick
                ? (s: any) => {
                    const row = s?.activePayload?.[0]?.payload;
                    if (row) onPointClick(xKey ? row[xKey] : undefined, row);
                  }
                : undefined
            }
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)" />
            <XAxis dataKey={xKey} tick={axisTick} />
            <YAxis tick={axisTick} />
            <Tooltip
              contentStyle={chartTooltipStyle}
              formatter={
                tooltipFormatter
                  ? (v: any, _name: any, p: any) =>
                      tooltipFormatter(v, series.find((s) => s.key === p?.dataKey) ?? series[0])
                  : undefined
              }
            />
            {series.map((s) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={toneToVar(s.tone)}
                strokeWidth={2}
                dot={{
                  r: 3,
                  cursor: onPointClick ? 'pointer' : undefined,
                  // Dot/activeDot onClick is adapted by recharts to
                  // `(mergedDotProps, event) => ...` — mergedDotProps carries
                  // `payload`, unlike the chart-level onClick which needs an
                  // active tooltip state first. Stop the bubble so the
                  // chart-level handler below doesn't double-fire.
                  onClick: onPointClick
                    ? (dotProps: any, event: React.MouseEvent) => {
                        event?.stopPropagation?.();
                        const row = dotProps?.payload;
                        if (row) onPointClick(xKey ? row[xKey] : undefined, row);
                      }
                    : undefined,
                }}
                activeDot={{
                  r: 4,
                  cursor: onPointClick ? 'pointer' : undefined,
                  onClick: onPointClick
                    ? (dotProps: any, event: React.MouseEvent) => {
                        event?.stopPropagation?.();
                        const row = dotProps?.payload;
                        if (row) onPointClick(xKey ? row[xKey] : undefined, row);
                      }
                    : undefined,
                }}
                connectNulls
              />
            ))}
          </LineChart>
        ) : (
          <PieChart>
            <Tooltip contentStyle={chartTooltipStyle} />
            <Pie
              data={data}
              dataKey={series[0]?.key}
              nameKey={xKey}
              innerRadius="60%"
              outerRadius="90%"
              paddingAngle={2}
            >
              {data.map((row, i) => (
                <Cell key={i} fill={toneToVar(row.tone ?? series[i % series.length]?.tone)} />
              ))}
            </Pie>
          </PieChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}
